import { toast } from "sonner";

import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { mutedLine } from "@/shared/muteChoices.ts";
import { useMuteSenderRequest, useUnmuteSender } from "./api.ts";

/**
 * Mute a sender or domain from a task (BIG-8853). Its mail is not read for
 * tasks again, and every task it asked for leaves the list at once. The
 * toast says how many, with Undo: the sender is read again and each task goes
 * back where it was.
 */
export function useMuteSender() {
  // The mute removed the row, so a failed Undo says so in a toast of its own.
  const unmute = useUnmuteSender({
    reportedByCaller: true,
    onError: (_error, req) =>
      errorToast(`Couldn't unmute ${req.value}.`, undefined, {
        actionText: "Retry",
        actionStatsigName: "tasks-mute-undo-retry",
        onAction: (toastId) => {
          toast.dismiss(toastId);
          unmute.mutate(req);
        },
      }),
  });
  // From the mutation, not `mutate`: the mute drops the row it came from.
  const muteRequest = useMuteSenderRequest(({ cancelledTaskIds }, { value }) =>
    confirmationToast(mutedLine(value, cancelledTaskIds.length), undefined, {
      actionText: "Undo",
      actionStatsigName: "tasks-mute-undo",
      onAction: (toastId) => {
        toast.dismiss(toastId);
        unmute.mutate({ value, reopenTaskIds: cancelledTaskIds });
      },
    }),
  );

  const mute = (value: string) => muteRequest.mutate({ value });

  return { mute, isPending: muteRequest.isPending, error: muteRequest.error ?? unmute.error };
}
